// Sección plegable de un sitio: un acordeón accesible (botón con
// aria-expanded y aria-controls) con flecha, título, contador opcional y sus
// bloques adentro. El aspecto es el de las secciones de configuración de una
// colección. Lo de adentro se monta la primera vez que se abre y después
// queda montado: una sección cerrada no pide los datos de sus bloques.

import { useEffect, useId, useState, type ReactNode } from "react";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

export interface SectionBlockProps {
  title: string;
  /** Contador junto al título (sin él, no se muestra). */
  count?: number;
  /** Abierta al cargar. */
  defaultOpen: boolean;
  /** La abre (p. ej. el editor eligió un bloque de adentro). */
  forceOpen?: boolean;
  children: ReactNode;
}

export function SectionBlock({ title, count, defaultOpen, forceOpen = false, children }: SectionBlockProps) {
  const bodyId = useId();
  const [open, setOpen] = useState(defaultOpen || forceOpen);
  const [mounted, setMounted] = useState(open);
  // En el editor «Abierta al cargar» cambia al momento.
  useEffect(() => setOpen(defaultOpen), [defaultOpen]);
  useEffect(() => {
    if (forceOpen) setOpen(true);
  }, [forceOpen]);
  useEffect(() => {
    if (open) setMounted(true);
  }, [open]);

  return (
    <section className="rounded-lg border border-border bg-card" data-testid="site-section" data-open={open || undefined}>
      <h2 className="text-sm">
        <button
          type="button"
          aria-expanded={open}
          aria-controls={bodyId}
          onClick={() => setOpen((v) => !v)}
          className={cn(
            "flex w-full min-w-0 items-center gap-2 rounded-lg px-4 py-3 text-left font-medium text-foreground transition-colors",
            "hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40",
            open && "rounded-b-none",
          )}
        >
          <ChevronRight className={cn("size-3.5 shrink-0 text-muted-foreground transition-transform", open && "rotate-90")} aria-hidden />
          <span className="min-w-0 break-words">{title}</span>
          {count !== undefined && (
            <>
              <span
                className="inline-flex h-5 shrink-0 items-center rounded-md border border-border px-1.5 text-[11px] font-normal tabular-nums text-muted-foreground"
                aria-hidden
                data-testid="site-section-count"
              >
                {count}
              </span>
              <span className="sr-only">({count === 1 ? "1 bloque" : `${count} bloques`})</span>
            </>
          )}
        </button>
      </h2>
      <div id={bodyId} role="region" aria-label={title} hidden={!open} className="@container border-t border-border p-4 @min-[40rem]:p-5">
        {mounted ? children : null}
      </div>
    </section>
  );
}
